import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table } from "antd";
import { NotificationManager } from "react-notifications";
import {
	QUALITY_ISSUE_TABLE_FIELDS,
	fetchQualityIssueMasterList,
	getQualityIssueDisplayValue,
} from "./kycQualityIssueShared";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

const getSortOrder = (tableSorter, columnKey) => {
	if (tableSorter.field !== columnKey) {
		return null;
	}
	const order = String(tableSorter.order || "").toUpperCase();
	if (order === "ASC") {
		return "ascend";
	}
	if (order === "DESC") {
		return "descend";
	}
	return null;
};

const formatRecordCount = (total, range) => {
	if (!total) {
		return "Showing 0 records";
	}
	return `Showing ${range[0]}-${range[1]} of ${total.toLocaleString()} records`;
};

const COLUMN_WIDTHS = {
	complaintNo: 180,
	topic: 220,
	complaintDate: 160,
	modeOfComplaint: 180,
	complaintType: 160,
	complaintSubType: 280,
	priority: 140,
	complaintDescription: 280,
	customer: 200,
	email: 220,
	mobile: 160,
	contact: 160,
	invoiceDate: 160,
	invoiceNo: 160,
	dispatchDate: 160,
	dispatchQty: 160,
	order: 160,
	orderRequestLine: 260,
	affectedQuantity: 180,
	assigneeSuggestion: 240,
	feedbackOfAssignee: 320,
	preventiveAction: 240,
	creditMemo: 160,
	generateCreditNote: 200,
	openDays: 130,
	complaintStatus: 160,
	material: 260,
	ownerid: 180,
};

const TABLE_SCROLL_X = QUALITY_ISSUE_TABLE_FIELDS.reduce(
	(total, field) => total + (COLUMN_WIDTHS[field.key] || 180),
	0
);

const getRowKey = (record, index) => {
	const complaintNo = record?.complaintNo || record?.complaintNumber;
	if (complaintNo) {
		return complaintNo;
	}
	const id = String(record?.id || "").trim();
	if (id && id !== EMPTY_GUID) {
		return id;
	}
	return index;
};

const KycMaterialQualityIssueTab = ({
	productName,
	search = "",
	isActive,
	navigationContext = {},
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [qualityIssueTable, setQualityIssueTable] = useState({
		rows: [],
		totalRecords: 0,
		currentPage: 1,
		pageSize: 10,
	});
	const [tableSorter, setTableSorter] = useState({
		field: "complaintDate",
		order: "DESC",
	});

	const isplCustomer = String(
		navigationContext.materialsCustomer || navigationContext.accountName || ""
	).trim();

	const loadQualityIssueList = useCallback(async () => {
		if (!productName) {
			setQualityIssueTable((prev) => ({ ...prev, rows: [], totalRecords: 0 }));
			return;
		}

		setIsLoading(true);
		try {
			const result = await fetchQualityIssueMasterList({
				ispl_product: productName,
				ispl_customer: isplCustomer,
				customer: isplCustomer,
				material: productName,
				materialName: navigationContext.materialName || productName,
				search,
				pageindex: qualityIssueTable.currentPage,
				pagesize: qualityIssueTable.pageSize,
				sortcolumn: tableSorter.field,
				orderBy: tableSorter.order,
				groupCompanyId: navigationContext.groupCompanyId || "",
			});

			setQualityIssueTable((prev) => ({
				...prev,
				rows: result.rows,
				totalRecords: result.totalRecords,
				currentPage: result.currentPage,
			}));
		} catch (error) {
			console.log(error, "Error");
			NotificationManager.error("Unable to load quality issue listing.");
			setQualityIssueTable((prev) => ({ ...prev, rows: [], totalRecords: 0 }));
		} finally {
			setIsLoading(false);
		}
	}, [
		productName,
		isplCustomer,
		search,
		qualityIssueTable.currentPage,
		qualityIssueTable.pageSize,
		tableSorter.field,
		tableSorter.order,
		navigationContext.groupCompanyId,
		navigationContext.materialName,
	]);

	useEffect(() => {
		if (!isActive || !productName) {
			return undefined;
		}
		loadQualityIssueList();
		return undefined;
	}, [isActive, productName, loadQualityIssueList]);

	useEffect(() => {
		if (!isActive) {
			return undefined;
		}
		setQualityIssueTable((prev) => ({ ...prev, currentPage: 1 }));
		return undefined;
	}, [search, isActive]);

	const handleTableAscDes = (pagination, _filters, sorter) => {
		const sortInfo = Array.isArray(sorter) ? sorter[0] || {} : sorter || {};
		const sortOrder = sortInfo.order;
		const rawField = sortInfo.field;
		const sortField =
			rawField !== undefined && rawField !== null && rawField !== ""
				? Array.isArray(rawField)
					? rawField[0]
					: rawField
				: tableSorter.field;
		const nextOrder =
			sortOrder === "ascend" ? "ASC" : sortOrder === "descend" ? "DESC" : tableSorter.order || "DESC";

		setQualityIssueTable((prev) => ({
			...prev,
			currentPage: pagination?.current ?? prev.currentPage,
			pageSize: pagination?.pageSize ?? prev.pageSize,
		}));

		if (sortField && (sortField !== tableSorter.field || nextOrder !== tableSorter.order)) {
			setTableSorter({ field: sortField, order: nextOrder });
		}
	};

	const listingPagination = {
		total: Number(qualityIssueTable.totalRecords || 0),
		current: Number(qualityIssueTable.currentPage || 1),
		pageSize: Number(qualityIssueTable.pageSize || 10),
		responsive: true,
		showSizeChanger: false,
		showTotal: formatRecordCount,
	};

	const tableColumns = useMemo(
		() =>
			QUALITY_ISSUE_TABLE_FIELDS.map((field) => ({
				title: field.label,
				dataIndex: field.key,
				key: field.key,
				sorter: true,
				showSorterTooltip: false,
				width: COLUMN_WIDTHS[field.key] || 180,
				sortOrder: getSortOrder(tableSorter, field.key),
				render: (_, record) => <span>{getQualityIssueDisplayValue(record, field)}</span>,
			})),
		[tableSorter]
	);

	return (
		<div className="kyc-quality-issue-listing">
			<Table
				bordered
				scroll={{ x: TABLE_SCROLL_X }}
				className="logistic_tables kyc_logistic_tables"
				dataSource={qualityIssueTable.rows}
				columns={tableColumns}
				sortDirections={["ascend", "descend"]}
				onChange={handleTableAscDes}
				rowKey={getRowKey}
				locale={{ emptyText: "No data found" }}
				pagination={listingPagination}
				loading={isLoading}
			/>
		</div>
	);
};

export default KycMaterialQualityIssueTab;
